/**
 * ============================================================
 * KEJJA OS — src/pages/library/ItemForm.tsx
 * Create/edit library items. Supports attachments.
 * ============================================================
 */

import { useEffect, useRef, useState, type ChangeEvent, type FormEvent } from 'react';
import {
  X, Trash2, Star, Paperclip, Upload, Download, File as FileIcon,
} from 'lucide-react';

import {
  useFolders, useItem, useCreateItem, useUpdateItem, useDeleteItem,
  useUploadAttachment, useDeleteAttachment,
} from '@/hooks/useLibrary';
import { libraryApi, type LibraryDocType } from '@/lib/libraryApi';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import ReadAttachmentModal from './ReadAttachmentModal';
import { BookOpen } from 'lucide-react';

const DOC_TYPES: Array<{ value: LibraryDocType; label: string; emoji: string }> = [
  { value: 'note', label: 'Note', emoji: '📝' },
  { value: 'id_document', label: 'ID Doc', emoji: '🪪' },
  { value: 'receipt', label: 'Receipt', emoji: '🧾' },
  { value: 'medical', label: 'Medical', emoji: '🏥' },
  { value: 'school', label: 'School', emoji: '🎓' },
  { value: 'certificate', label: 'Cert', emoji: '📜' },
  { value: 'contract', label: 'Contract', emoji: '✍️' },
  { value: 'photo', label: 'Photo', emoji: '🖼️' },
  { value: 'link', label: 'Link', emoji: '🔗' },
  { value: 'other', label: 'Other', emoji: '📦' },
];

interface Props {
  homeId: string;
  itemId: string | null;
  hint: { folderId?: string | null; ownerScope?: 'shared' | 'personal' } | null;
  onClose: () => void;
}

interface FormState {
  title: string;
  body: string;
  docType: LibraryDocType;
  tags: string;               // comma-separated
  folderId: string | null;
  ownerScope: 'shared' | 'personal';
  isFavorite: boolean;
  metadata: Record<string, string>;   // per-type string fields, converted to object on save
}

const EMPTY_FORM: FormState = {
  title: '', body: '', docType: 'note', tags: '',
  folderId: null, ownerScope: 'shared', isFavorite: false,
  metadata: {},
};

// Per doc-type metadata fields
const METADATA_FIELDS: Partial<Record<LibraryDocType, Array<{ key: string; label: string; type?: 'date' | 'text' | 'number' }>>> = {
  id_document: [
    { key: 'documentType', label: 'Type (National ID / Passport / KRA PIN)' },
    { key: 'number', label: 'Number' },
    { key: 'holderName', label: 'Holder name' },
    { key: 'expiresOn', label: 'Expires on', type: 'date' },
  ],
  receipt: [
    { key: 'merchant', label: 'Merchant' },
    { key: 'amount', label: 'Amount', type: 'number' },
    { key: 'purchasedOn', label: 'Purchased on', type: 'date' },
    { key: 'warrantyUntil', label: 'Warranty until', type: 'date' },
  ],
  medical: [
    { key: 'patientName', label: 'Patient' },
    { key: 'provider', label: 'Doctor / Hospital' },
    { key: 'visitDate', label: 'Visit date', type: 'date' },
    { key: 'visitType', label: 'Type (checkup / prescription / vaccine)' },
  ],
  school: [
    { key: 'studentName', label: 'Student' },
    { key: 'term', label: 'Term (Term 1 / Q2)' },
    { key: 'year', label: 'Year', type: 'number' },
    { key: 'subject', label: 'Subject' },
  ],
  certificate: [
    { key: 'issuedBy', label: 'Issued by' },
    { key: 'holder', label: 'Awarded to' },
    { key: 'issuedOn', label: 'Issued on', type: 'date' },
  ],
  contract: [
    { key: 'counterparty', label: 'With' },
    { key: 'signedOn', label: 'Signed on', type: 'date' },
    { key: 'endsOn', label: 'Ends on', type: 'date' },
  ],
  link: [
    { key: 'url', label: 'URL' },
  ],
};

export function ItemForm({ homeId, itemId, hint, onClose }: Props) {
  const { data: foldersData } = useFolders(homeId);
  const { data: itemData } = useItem(homeId, itemId ?? undefined);
  const createMut = useCreateItem(homeId);
  const updateMut = useUpdateItem(homeId);
  const deleteMut = useDeleteItem(homeId);
  const uploadMut = useUploadAttachment(homeId);
  const deleteAtt = useDeleteAttachment(homeId);

  const [form, setForm] = useState<FormState>(() => ({
    ...EMPTY_FORM,
    folderId: hint?.folderId ?? null,
    ownerScope: hint?.ownerScope ?? 'shared',
  }));
  const [err, setErr] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const [readingAttId, setReadingAttId] = useState<string | null>(null);
  const [readingFileName, setReadingFileName] = useState<string>('');

  useEffect(() => {
    if (itemData?.item) {
      const i = itemData.item;
      setForm({
        title: i.title,
        body: i.body ?? '',
        docType: i.docType,
        tags: (i.tags ?? []).join(', '),
        folderId: i.folderId,
        ownerScope: i.ownerUserId ? 'personal' : 'shared',
        isFavorite: i.isFavorite,
        metadata: Object.fromEntries(
          Object.entries(i.metadata ?? {}).map(([k, v]) => [k, String(v ?? '')]),
        ),
      });
    }
  }, [itemData]);

  const folders = (foldersData?.folders ?? []).filter((f) =>
    form.ownerScope === 'personal' ? f.ownerUserId !== null : f.ownerUserId === null,
  );

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setErr(null);
    if (!form.title.trim()) { setErr('Title is required.'); return; }

    const metadata: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(form.metadata)) {
      if (v !== undefined && v !== null && v !== '') metadata[k] = v;
    }

    const payload = {
      title: form.title.trim(),
      body: form.body.trim() || null,
      docType: form.docType,
      tags: form.tags.split(',').map((t) => t.trim()).filter(Boolean),
      metadata,
      folderId: form.folderId,
      ownerUserId: form.ownerScope === 'personal' ? undefined : null,
      isFavorite: form.isFavorite,
    };

    const onDone = () => onClose();
    const onError = (e: Error) => setErr(e.message ?? 'Could not save.');

    if (itemId) {
      updateMut.mutate({ itemId, patch: payload }, { onSuccess: onDone, onError });
    } else {
      createMut.mutate(payload, { onSuccess: onDone, onError });
    }
  };

  const handleDelete = () => {
    if (!itemId) return;
    if (!confirm('Delete this item? This can\'t be undone.')) return;
    deleteMut.mutate(itemId, { onSuccess: onClose });
  };

  const handleFilePicked = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !itemId) return;
    setErr(null);
    uploadMut.mutate({ itemId, file }, {
      onError: (e: Error) => setErr(e.message ?? 'Upload failed.'),
      onSuccess: () => {
        if (fileRef.current) fileRef.current.value = '';
      },
    });
  };

  const attachments = itemData?.item?.attachments ?? [];
  const metadataFields = METADATA_FIELDS[form.docType] ?? [];

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <Card className="w-full max-w-xl my-8 max-h-[calc(100vh-4rem)] overflow-y-auto">
        <CardContent className="p-5">
          <form onSubmit={submit} className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-bold">{itemId ? 'Edit' : 'New item'}</h2>
              <Button type="button" size="sm" variant="ghost" onClick={onClose}>
                <X className="w-4 h-4" />
              </Button>
            </div>

            {/* Scope selector (only for new items) */}
            {!itemId && (
              <div className="flex gap-2">
                {(['shared', 'personal'] as const).map((s) => (
                  <button
                    key={s} type="button"
                    onClick={() => setForm({ ...form, ownerScope: s, folderId: null })}
                    className={cn(
                      'flex-1 py-2 rounded-lg border text-xs font-semibold capitalize',
                      form.ownerScope === s
                        ? 'border-purple-500/60 bg-purple-500/10 text-purple-700 dark:text-purple-300'
                        : 'border-border text-muted-foreground',
                    )}
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}

            {/* Title + favorite */}
            <div className="grid grid-cols-[1fr_auto] gap-2">
              <Input
                autoFocus
                value={form.title}
                onChange={(e: ChangeEvent<HTMLInputElement>) => setForm({ ...form, title: e.target.value })}
                placeholder="Title"
                className="text-base"
              />
              <Button
                type="button"
                variant={form.isFavorite ? 'default' : 'outline'}
                className={cn('h-9 w-9 p-0', form.isFavorite && 'bg-rose-600 hover:bg-rose-700 text-white')}
                onClick={() => setForm({ ...form, isFavorite: !form.isFavorite })}
              >
                <Star className={cn('w-4 h-4', form.isFavorite && 'fill-current')} />
              </Button>
            </div>

            {/* Doc type */}
            <div>
              <Label className="text-xs">Type</Label>
              <div className="flex gap-1 flex-wrap mt-1">
                {DOC_TYPES.map((d) => (
                  <button
                    key={d.value}
                    type="button"
                    onClick={() => setForm({ ...form, docType: d.value, metadata: {} })}
                    className={cn(
                      'flex items-center gap-1 px-2 py-1 rounded-md border text-[11px] font-medium transition-all',
                      form.docType === d.value
                        ? 'border-purple-500/60 bg-purple-500/10 text-purple-700 dark:text-purple-300'
                        : 'border-border text-muted-foreground',
                    )}
                  >
                    <span>{d.emoji}</span>
                    <span>{d.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Type-specific metadata */}
            {metadataFields.length > 0 && (
              <div className="bg-muted/30 border border-border rounded-lg p-3 space-y-2">
                <Label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                  {form.docType.replace('_', ' ')} details
                </Label>
                <div className="grid grid-cols-2 gap-2">
                  {metadataFields.map((f) => (
                    <div key={f.key} className={f.key === 'url' ? 'col-span-2' : ''}>
                      <Label className="text-[10px]">{f.label}</Label>
                      <Input
                        type={f.type ?? 'text'}
                        value={form.metadata[f.key] ?? ''}
                        onChange={(e: ChangeEvent<HTMLInputElement>) =>
                          setForm({ ...form, metadata: { ...form.metadata, [f.key]: e.target.value } })
                        }
                        className="mt-1 h-8 text-xs"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Folder */}
            <div>
              <Label className="text-xs">Folder</Label>
              <select
                value={form.folderId ?? ''}
                onChange={(e) => setForm({ ...form, folderId: e.target.value || null })}
                className="mt-1 w-full h-9 rounded-md border border-border bg-background px-2 text-sm"
              >
                <option value="">No folder</option>
                {folders.map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.icon ? `${f.icon} ` : ''}{f.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Tags */}
            <div>
              <Label className="text-xs">Tags</Label>
              <Input
                value={form.tags}
                onChange={(e: ChangeEvent<HTMLInputElement>) => setForm({ ...form, tags: e.target.value })}
                placeholder="Comma-separated (e.g. urgent, 2026, rental)"
                className="mt-1"
              />
            </div>

            {/* Body */}
            <div>
              <Label className="text-xs">Notes</Label>
              <textarea
                value={form.body}
                onChange={(e) => setForm({ ...form, body: e.target.value })}
                placeholder="Details, markdown supported…"
                className="w-full mt-1 min-h-[100px] rounded-md border border-border bg-background p-2 text-xs focus:outline-none focus:ring-2 focus:ring-purple-500/40 focus:border-purple-500"
              />
            </div>

            {/* Attachments */}
            {itemId && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-xs flex items-center gap-1.5">
                    <Paperclip className="w-3 h-3" /> Attachments
                  </Label>
                  <Button
                    type="button" size="sm" variant="ghost"
                    className="h-6 text-[11px]"
                    onClick={() => fileRef.current?.click()}
                    disabled={uploadMut.isPending}
                  >
                    <Upload className="w-3 h-3 mr-1" />
                    {uploadMut.isPending ? 'Uploading…' : 'Upload file'}
                  </Button>
                  <input
                    ref={fileRef} type="file" className="hidden"
                    onChange={handleFilePicked}
                  />
                </div>
                <div className="space-y-1">
                  {attachments.length === 0 && (
                    <p className="text-[11px] text-muted-foreground italic">No attachments yet. Max 25MB per file.</p>
                  )}
                  {attachments.map((a) => (
                    <div key={a.id} className="flex items-center gap-2 p-2 rounded-md bg-muted/30 border border-border">
                      <FileIcon className="w-3.5 h-3.5 text-purple-600 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-medium truncate">{a.fileName}</div>
                        <div className="text-[10px] text-muted-foreground">
                          {(a.sizeBytes / 1024).toFixed(0)} KB · {a.mimeType}
                        </div>
                      </div>
                      {a.mimeType === 'application/pdf' && (
                        <button
                          type="button"
                          onClick={() => { setReadingAttId(a.id); setReadingFileName(a.fileName); }}
                          className="text-[11px] text-purple-600 hover:underline flex items-center gap-0.5"
                        >
                          <BookOpen className="w-3 h-3" /> Read
                        </button>
                      )}
                      <a
                        href={libraryApi.downloadUrl(homeId, a.id)}
                        target="_blank" rel="noreferrer"
                        className="text-[11px] text-purple-600 hover:underline flex items-center gap-0.5"
                      >
                        <Download className="w-3 h-3" /> Open
                      </a>
                      <button
                        type="button"
                        onClick={() => {
                          if (confirm('Delete this attachment?')) deleteAtt.mutate(a.id);
                        }}
                        className="text-muted-foreground hover:text-rose-600"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {err && <div className="text-xs text-rose-600 font-medium">{err}</div>}

            <div className="flex gap-2 pt-2 border-t border-border">
              <Button
                type="submit"
                className="bg-purple-600 hover:bg-purple-700 text-white flex-1"
                disabled={createMut.isPending || updateMut.isPending}
              >
                {createMut.isPending || updateMut.isPending
                  ? 'Saving…'
                  : itemId ? 'Save changes' : 'Create'}
              </Button>
              {itemId && (
                <Button
                  type="button" variant="ghost"
                  className="text-rose-600 hover:text-rose-700"
                  onClick={handleDelete}
                  disabled={deleteMut.isPending}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              )}
            </div>
          </form>
        </CardContent>
      </Card>
      {readingAttId && (
        <ReadAttachmentModal
          homeId={homeId}
          attachmentId={readingAttId}
          fileName={readingFileName}
          onClose={() => setReadingAttId(null)}
        />
      )}
    </div>
  );
}
